import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './ProductDetailsPage.css';
import { API_BASE, getAssetUrl } from '../config/api';
import LoadingSpinner from '../components/LoadingSpinner';

const ProductDetailsPage = ({ onAddToCart }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState(null);
  const [showSustainability, setShowSustainability] = useState(false);
  const [showProductCare, setShowProductCare] = useState(false);
  const [showInStoreService, setShowInStoreService] = useState(false);
  const [showGifting, setShowGifting] = useState(false);
  const [giftWrapping, setGiftWrapping] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchProduct();
  }, [id]);

  // Auto-select the first color variant if the product has no default images
  useEffect(() => {
    if (product && product.colorVariants && product.colorVariants.length > 0) {
      const hasDefaultImages = product.images && product.images.length > 0;
      if (!hasDefaultImages) {
        setSelectedColor(0);
      }
    }
  }, [product]);

  const fetchProduct = async () => {
    try {
      const response = await fetch(`${API_BASE}/products/${id}`);
      const data = await response.json();
      if (data.success) {
        setProduct(data.data);
      } else {
        console.error('Product not found');
      }
    } catch (error) {
      console.error('Error fetching product:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (product && onAddToCart) {
      // Check if size is required but not selected
      if (product.sizeType && product.sizeType !== 'NONE' && !selectedSize) {
        alert('Please select a size');
        return;
      }

      // Check if color is required but not selected
      if (product.colorVariants && product.colorVariants.length > 0 && selectedColor === null) {
        alert('Please select a color');
        return;
      }

      const activeVariant = selectedColor !== null && product.colorVariants
        ? product.colorVariants[selectedColor]
        : null;

      // Build a unique cart id that includes color and size
      const colorKey = activeVariant ? activeVariant.colorName : 'no-color';
      const sizeKey = selectedSize || 'no-size';

      const productWithOptions = {
        ...product,
        selectedSize: selectedSize || null,
        selectedColor: activeVariant ? activeVariant.colorName : null,
        selectedColorHex: activeVariant ? activeVariant.colorHex : null,
        // Use color-specific images for display in cart
        cartImage: activeVariant && activeVariant.images && activeVariant.images.length > 0
          ? activeVariant.images[0]
          : (product.images && product.images.length > 0 ? product.images[0] : null),
        id: `${product._id || product.id}-${colorKey}-${sizeKey}`
      };
      onAddToCart(productWithOptions);
    }
  };

  if (!product || loading) {
    return <LoadingSpinner />;
  }

  // Get product images: selected color → default images → first color variant images
  const activeColorVariant = selectedColor !== null && product.colorVariants && product.colorVariants[selectedColor];
  const productImages = (() => {
    if (activeColorVariant && activeColorVariant.images && activeColorVariant.images.length > 0) {
      return activeColorVariant.images.map(img => getAssetUrl(img));
    }
    if (product.images && product.images.length > 0) {
      return product.images.map(img => getAssetUrl(img));
    }
    // Fallback: show first color variant's images so something is always visible
    if (product.colorVariants) {
      for (const variant of product.colorVariants) {
        if (variant.images && variant.images.length > 0) {
          return variant.images.map(img => getAssetUrl(img));
        }
      }
    }
    return [];
  })();

  const handleColorSelect = (index) => {
    if (selectedColor === index) {
      // Deselect only if there are default images to fall back to
      const hasDefaultImages = product.images && product.images.length > 0;
      if (hasDefaultImages) {
        setSelectedColor(null);
      }
    } else {
      setSelectedColor(index);
    }
    setSelectedImage(0);
  };

  return (
    <div className="product-details-page">
      <div className="product-details-container">
        {/* Left Side - Product Images */}
        <div className="product-images-section">
          <div className="main-product-image">
            {productImages.length > 0 ? (
              <img 
                src={productImages[selectedImage]} 
                alt={product.name}
              />
            ) : (
              <div className="placeholder-image" style={{ background: product.gradient }}>
                <span>{product.name}</span>
              </div>
            )}
          </div>
          
          {productImages.length > 1 && (
            <div className="product-thumbnails">
              {productImages.map((img, index) => (
                <div 
                  key={index}
                  className={`thumbnail ${selectedImage === index ? 'active' : ''}`}
                  onClick={() => setSelectedImage(index)}
                >
                  <img src={img} alt={`${product.name} ${index + 1}`} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right Side - Product Info */}
        <div className="product-info-section">
          <div className="product-info-header">
            <div>
              {product.categoryLabel && (
                <p className="product-code">{product.categoryLabel}</p>
              )}
              {product.badge && (
                <span className="product-badge-detail">{product.badge}</span>
              )}
              <h1 className="product-title">{product.name}</h1>
              <p className="product-price-detail">
                ৳{product.price}
                {product.originalPrice && (
                  <span className="original-price-detail">৳{product.originalPrice}</span>
                )}
              </p>
            </div>
            <button className="wishlist-btn-detail">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
              </svg>
            </button>
          </div>

          {/* Color Selection */}
          {product.colorVariants && product.colorVariants.length > 0 && (
            <div className="color-selection">
              <h3 className="color-title">
                Color{selectedColor !== null && product.colorVariants[selectedColor]
                  ? `: ${product.colorVariants[selectedColor].colorName}`
                  : ''}
              </h3>
              <div className="color-options">
                {product.colorVariants.map((variant, index) => (
                  <button
                    key={index}
                    className={`color-option ${selectedColor === index ? 'selected' : ''}`}
                    onClick={() => handleColorSelect(index)}
                    title={variant.colorName}
                  >
                    <span
                      className="color-swatch-customer"
                      style={{ backgroundColor: variant.colorHex }}
                    />
                    <span className="color-option-label">{variant.colorName}</span>
                  </button>
                ))}
              </div>
              {selectedColor === null && (
                <p className="color-hint">Please select a color before adding to cart</p>
              )}
            </div>
          )}

          {/* Size Selection */}
          {product.sizeType && product.sizeType !== 'NONE' && product.availableSizes && product.availableSizes.length > 0 && (
            <div className="size-selection">
              <h3 className="size-title">
                Select Size ({product.sizeType === 'CLOTHING' ? 'Clothing' : 'Shoes'})
              </h3>
              <div className="size-options">
                {product.availableSizes.map(size => (
                  <button
                    key={size}
                    className={`size-option ${selectedSize === size ? 'selected' : ''}`}
                    onClick={() => setSelectedSize(size)}
                  >
                    {size}
                  </button>
                ))}
              </div>
              {!selectedSize && (
                <p className="size-hint">Please select a size before adding to cart</p>
              )}
            </div>
          )}

          <button className="add-to-cart-btn" onClick={handleAddToCart}>Place in Cart</button>
          
         
          
         

          {product.description && (
            <div className="product-description">
              <p>{product.description}</p>
              <button className="read-more-btn">Read more</button>
            </div>
          )}

          {/* Accordion Sections */}
         
        </div>
      </div>
    </div>
  );
};

export default ProductDetailsPage;
